import { useEffect, useState } from 'react';
import { getGlobalLists, createGlobalList, updateGlobalList, deleteGlobalList, addGlobalListItem, updateGlobalListItem, deleteGlobalListItem } from '@/lib/api-global-lists';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, List, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const GlobalLists = () => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newListForm, setNewListForm] = useState({ key: '', label_de: '', label_tr: '' });
  const [itemForm, setItemForm] = useState({ value: '', label_de: '', label_tr: '', is_active: true });

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    try {
      const data = await getGlobalLists();
      setLists(data);
      // Update selected list if it exists
      if (selectedList) {
        const updated = data.find(l => l.key === selectedList.key);
        if (updated) setSelectedList(updated);
      } else if (data.length > 0) {
        setSelectedList(data[0]);
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden der Listen');
    } finally {
      setLoading(false);
    }
  };

  // Sanitize key on input
  const handleKeyInput = (value) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setNewListForm({ ...newListForm, key: sanitized });
  };

  const handleCreateList = async () => {
    const { key, label_de, label_tr } = newListForm;
    
    // Validation
    if (!key || key.length < 3) {
      toast.error('Key muss mindestens 3 Zeichen haben');
      return;
    }
    if (!label_de) {
      toast.error('Deutsche Bezeichnung ist erforderlich');
      return;
    }
    
    setSaving(true);
    try {
      await createGlobalList({
        key: key,
        label_de: label_de,
        label_tr: label_tr || label_de
      });
      toast.success('Liste erstellt');
      setDialogOpen(false);
      setNewListForm({ key: '', label_de: '', label_tr: '' });
      loadLists();
    } catch (error) {
      console.error('Create list error:', error);
      const msg = error.response?.data?.detail || 'Fehler beim Erstellen der Liste';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteList = async () => {
    if (!selectedList) return;
    if (!window.confirm(`Liste "${selectedList.label_de}" wirklich löschen?`)) return;
    
    try {
      await deleteGlobalList(selectedList.key);
      toast.success('Liste gelöscht');
      setSelectedList(null);
      loadLists();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleAddItem = async () => {
    if (!selectedList || !itemForm.value) return;
    try {
      await addGlobalListItem(selectedList.key, itemForm);
      toast.success('Eintrag hinzugefügt');
      setItemDialogOpen(false);
      setItemForm({ value: '', label_de: '', label_tr: '', is_active: true });
      loadLists();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleUpdateItem = async () => {
    if (!selectedList || !editingItem) return;
    try {
      await updateGlobalListItem(selectedList.key, editingItem.value, itemForm);
      toast.success('Eintrag aktualisiert');
      setItemDialogOpen(false);
      setEditingItem(null);
      setItemForm({ value: '', label_de: '', label_tr: '', is_active: true });
      loadLists();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteItem = async (value) => {
    if (!selectedList || !window.confirm('Eintrag deaktivieren?')) return;
    try {
      await deleteGlobalListItem(selectedList.key, value);
      toast.success('Eintrag deaktiviert');
      loadLists();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setItemForm({
      value: item.value,
      label_de: item.label_de,
      label_tr: item.label_tr,
      is_active: item.is_active
    });
    setItemDialogOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Globale Stammdaten</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie wiederverwendbare Listen für Dropdowns</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" />Neue Liste</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Neue Liste erstellen</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Schlüssel (Key) *</Label>
                <Input 
                  value={newListForm.key} 
                  onChange={(e) => handleKeyInput(e.target.value)} 
                  placeholder="z.B. countries, regions" 
                  data-testid="new-list-key"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nur Kleinbuchstaben, Zahlen und Unterstriche. Min. 3 Zeichen.
                </p>
              </div>
              <div>
                <Label>Bezeichnung (Deutsch) *</Label>
                <Input 
                  value={newListForm.label_de} 
                  onChange={(e) => setNewListForm({ ...newListForm, label_de: e.target.value })} 
                  placeholder="z.B. Länder"
                  data-testid="new-list-label-de"
                />
              </div>
              <div>
                <Label>Bezeichnung (Türkisch)</Label>
                <Input 
                  value={newListForm.label_tr} 
                  onChange={(e) => setNewListForm({ ...newListForm, label_tr: e.target.value })} 
                  placeholder="z.B. Ülkeler"
                  data-testid="new-list-label-tr"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
              <Button 
                onClick={handleCreateList} 
                disabled={saving || newListForm.key.length < 3 || !newListForm.label_de}
                data-testid="create-list-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Erstellen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-4 gap-6">
        <Card className="col-span-1">
          <CardHeader><CardTitle>Listen</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {lists.map((list) => (
              <button
                key={list.key}
                onClick={() => setSelectedList(list)}
                className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 ${
                  selectedList?.key === list.key ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                }`}
              >
                <List className="h-4 w-4" />
                {list.label_de}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{selectedList ? selectedList.label_de : 'Keine Liste ausgewählt'}</CardTitle>
                {selectedList && (
                  <p className="text-sm text-gray-500 mt-1">Key: {selectedList.key}</p>
                )}
              </div>
              {selectedList && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-red-600" onClick={handleDeleteList}>
                    <Trash2 className="w-4 h-4 mr-2" />Liste löschen
                  </Button>
                  <Dialog open={itemDialogOpen} onOpenChange={(open) => { setItemDialogOpen(open); if (!open) { setEditingItem(null); setItemForm({ value: '', label_de: '', label_tr: '', is_active: true }); } }}>
                    <DialogTrigger asChild>
                      <Button size="sm"><Plus className="w-4 h-4 mr-2" />Eintrag</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{editingItem ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Wert (Value) *</Label>
                          <Input value={itemForm.value} onChange={(e) => setItemForm({ ...itemForm, value: e.target.value })} disabled={!!editingItem} />
                        </div>
                        <div>
                          <Label>Bezeichnung (Deutsch)</Label>
                          <Input value={itemForm.label_de} onChange={(e) => setItemForm({ ...itemForm, label_de: e.target.value })} />
                        </div>
                        <div>
                          <Label>Bezeichnung (Türkisch)</Label>
                          <Input value={itemForm.label_tr} onChange={(e) => setItemForm({ ...itemForm, label_tr: e.target.value })} />
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={itemForm.is_active} onChange={(e) => setItemForm({ ...itemForm, is_active: e.target.checked })} className="rounded" />
                          <Label>Aktiv</Label>
                        </div>
                        <Button onClick={editingItem ? handleUpdateItem : handleAddItem} disabled={!itemForm.value}>
                          {editingItem ? 'Aktualisieren' : 'Hinzufügen'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedList ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Wert</TableHead>
                    <TableHead>Deutsch</TableHead>
                    <TableHead>Türkisch</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedList.items?.map((item) => (
                    <TableRow key={item.value}>
                      <TableCell className="font-medium">{item.value}</TableCell>
                      <TableCell>{item.label_de}</TableCell>
                      <TableCell>{item.label_tr}</TableCell>
                      <TableCell>{item.is_active ? <span className="text-green-600">Aktiv</span> : <span className="text-gray-400">Inaktiv</span>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(item)}><Pencil className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.value)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-gray-500 py-8">Wählen Sie eine Liste aus</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GlobalLists;